import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// New-UI action bar for InvoiceDocumentEditor. The buttons are the editor's own elements, with their own
// visibility rules; this only arranges them: one primary action, a few secondary ones, and the rest under More.
const visibleButtons = el => (el ? [...el.querySelectorAll('button')].filter(b => b.style.display !== 'none').length : 0);
const list = items => items.filter(Boolean).map((node, i) => <React.Fragment key={i}>{node}</React.Fragment>);
// R24: in More, Delete comes last, after a divider, so it isn't the first thing (or the first Tab stop) in the menu.
const isDanger = node => !!(node && node.props && /btn-danger/.test(node.props.className || ''));
const dangerLast = items => {
	const safe = items.filter(n => n && !isDanger(n));
	const danger = items.filter(isDanger);
	return danger.length ? [...safe, <span key="sep" className="apw2-ed-more-sep" role="separator" />, ...danger] : safe;
};

export default function EditorActionBar({ left, more = [], secondary = [], primary, dirty }) {
	const [open, setOpen] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const moreRef = useRef(null);
	const toggleRef = useRef(null);

	// A More item hides itself with style.display, as in Classic; hide More when nothing in it is visible.
	useLayoutEffect(() => {
		const n = visibleButtons(moreRef.current) > 0;
		if (n !== hasMore) setHasMore(n);
	});

	useEffect(() => {
		if (!open) return undefined;
		const onDown = e => { if (!moreRef.current?.contains(e.target) && !toggleRef.current?.contains(e.target)) setOpen(false); };
		// Capture phase: the editor's MUI Dialog stops Escape from bubbling to the document.
		const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); toggleRef.current?.focus(); } };
		document.addEventListener('mousedown', onDown);
		document.addEventListener('keydown', onKey, true);
		return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
	}, [open]);

	return (
		<div className="apw2-ed-bar" role="toolbar" aria-label="Invoice actions">
			<div className="apw2-ed-bar-l">
				{dirty && <span className="apw2-ed-dirty">Unsaved changes</span>}
			</div>
			<div className="apw2-ed-bar-r">
				{/* Close sits with the other actions, so it is a short move from them (it used to be at the far left). */}
				{left}
				<div className="apw2-ed-more-wrap" hidden={!hasMore}>
					<button ref={toggleRef} type="button" className="apw2-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>
						More<ExpandMoreIcon fontSize="inherit" />
					</button>
					<div ref={moreRef} className={'apw2-ed-more' + (open ? ' open' : '')} role="menu" aria-label="More actions"
						onClick={e => { if (e.target.closest && e.target.closest('button')) setOpen(false); }}>
						{list(dangerLast(more))}
					</div>
				</div>
				<span className="apw2-ed-sec">{list(secondary)}</span>
				{primary ? <span className="apw2-ed-pri">{primary}</span> : null}
			</div>
		</div>
	);
}
